import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { router } from '../..';
import { client } from '../config';
import { AppRoute } from '../constants/route';
import { ApiResponseStatus } from '../constants/server';
import {
  ApiResponse,
  RefreshResponseData,
  ShipmentResponseData,
} from '../models/apiResponse';
import { store } from '../stores/store';
import { generateLocaleRoute } from '../utils/uri/uri.utils';
import { loadVersion } from './versionService';

const REFRESH_TOKEN_URL = '/v1/refresh';

// Api here means our original backend implemented by Leonid and team
// Gateway - the new API provided by Andrii Ploskiy

export const addApiRequestInterceptor = (httpClient: AxiosInstance) => {
  httpClient.interceptors.request.use(async (config) => {
    const token = store.commonStore.isGuestParcelCreation
      ? store.commonStore.guestAccessToken
      : store.commonStore.accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const version = await loadVersion();

    config.headers.set(
      'Platform',
      `web; client: ${client}; app-version: ${version}`
    );

    const language =
      store.localizationsStore.selectedLocalization?.code.toLowerCase();
    const country =
      store.localizationsStore.selectedCountry?.country_code.toUpperCase();

    if (language) {
      config.headers.set('Language', language);
    }

    if (country) {
      config.headers.set('Country', country);
    }

    return config;
  });
};

export const addGatewayRequestInterceptor = (httpClient: AxiosInstance) => {
  httpClient.interceptors.request.use((config) => {
    // Guest token MUSTN NOT be used for gateway
    const token = store.commonStore.accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const language =
      store.localizationsStore.selectedLocalization?.code.toLowerCase();
    const country =
      store.localizationsStore.selectedCountry?.country_code.toUpperCase();

    if (language && country) {
      config.headers.set('Accept-Language', `${language}-${country}`);
    }

    return config;
  });
};

const refreshGuestTokens = async (httpClient: AxiosInstance) => {
  const response = await httpClient.post<ApiResponse<RefreshResponseData>>(
    REFRESH_TOKEN_URL,
    {}
  );

  return {
    accessToken: response.data.data?.token,
    refreshToken: response.data.data?.refresh_token,
  };
};

const handleServerError = (error: AxiosError) => {
  if (store.commonStore.isOffline) {
    store.commonStore.toastOffline();
  } else {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (!window.location.pathname.includes(AppRoute.PARCEL_CREATE)) {
      router.navigate(generateLocaleRoute(AppRoute.SERVER_ERROR));
    }
  }
};

export const addApiResponseInterceptor = (httpClient: AxiosInstance) => {
  httpClient.interceptors.response.use(
    async (response) => {
      if (window.location.pathname.includes(AppRoute.PARCEL_CREATE)) {
        const apiResponse = response.data as
          | ApiResponse<ShipmentResponseData>
          | undefined;

        store.parcelCreationStore.handleMessages(
          apiResponse?.message_bus,
          apiResponse?.data?.shipment
        );
      }

      return response;
    },
    async (error: AxiosError) => {
      // Currently in all cases error is handled and logged in one of the stores in handleError method.

      // Error interception logic may be injected here in future.

      console.error('error', error);

      // in case we received not expected error then default to 500
      const status = (error.response as AxiosResponse)?.status ?? 500;

      switch (status) {
        case ApiResponseStatus.BAD_REQUEST:
          // alert('bad request: ' + data.message);
          break;
        case ApiResponseStatus.UNAUTHORIZED:
          {
            // "Old" (non oauth) refresh is only required for the Guest flow.
            if (!store.commonStore.isGuestParcelCreation) {
              return Promise.reject(error);
            }

            const originalRequest = error.config;

            if (originalRequest?.url !== REFRESH_TOKEN_URL) {
              const { accessToken, refreshToken } =
                await refreshGuestTokens(httpClient);

              store.commonStore.setGuestAccessToken(accessToken!);
              store.commonStore.setGuestRefreshToken(refreshToken!);
              originalRequest!.headers!.Authorization = `Bearer ${accessToken}`;

              return httpClient(originalRequest!);
            }

            // TODO: probably not required
            store.userStore.logout({
              omitApiLogout: true,
            });
          }
          break;
        case ApiResponseStatus.FORBIDDEN:
          break;
        case ApiResponseStatus.NOT_FOUND:
          break;
        case ApiResponseStatus.VALIDATION_ERROR:
          break;
        case ApiResponseStatus.SERVER_ERROR:
          handleServerError(error);
          break;
        default:
          break;
      }

      return Promise.reject(error);
    }
  );
};

export const addGatewayResponseInterceptor = (httpClient: AxiosInstance) => {
  httpClient.interceptors.response.use(undefined, async (error: AxiosError) => {
    // Currently in all cases error is handled and logged in one of the stores in handleError method.

    // Error interception logic may be injected here in future.

    console.error('error', error);

    // in case we received not expected error then default to 500
    const status = (error.response as AxiosResponse)?.status ?? 500;

    switch (status) {
      case ApiResponseStatus.BAD_REQUEST:
        // alert('bad request: ' + data.message);
        break;
      case ApiResponseStatus.UNAUTHORIZED:
        break;
      case ApiResponseStatus.FORBIDDEN:
        break;
      case ApiResponseStatus.NOT_FOUND:
        break;
      case ApiResponseStatus.VALIDATION_ERROR:
        break;
      case ApiResponseStatus.SERVER_ERROR:
        handleServerError(error);
        break;
      default:
        break;
    }

    return Promise.reject(error);
  });
};
